import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import AnonBookingRoleWorkerDto from '@/models/AnonBookingRoleWorkers/AnonBookingRoleWorkerDto';
import { AppModule } from './app';
import UpdateAnonWorkerBookingOfferRequest from '@/models/AnonBookingRoleWorkers/UpdateAnonWorkerBookingOfferRequest';
import UpdateAnonWorkerBookingConfirmationRequest from '@/models/AnonBookingRoleWorkers/UpdateAnonWorkerBookingConfirmationRequest';
import ErrorMessage from '@/models/Shared/ErrorMessage';
import Errors from '@/models/Shared/Errors';
import AnonJobWorkerInviteDto from '@/models/AnonJobWorkers/AnonJobWorkerInviteDto';
import UpdateAnonWorkerJobInviteRequest from '@/models/AnonJobWorkers/UpdateAnonWorkerJobInviteRequest';
import AnonJobWorkerInterviewDto from '@/models/AnonJobWorkers/AnonJobWorkerInterviewDto';
import AnonJobWorkerInterviewWorkerConfirmationReply from '@/models/AnonJobWorkers/AnonJobWorkerInterviewWorkerConfirmationReply';
import AnonJobWorkerOfferDto from '@/models/AnonJobWorkers/AnonJobWorkerOfferDto';
import AnonJobWorkerOfferWorkerAcceptanceReply from '@/models/AnonJobWorkers/AnonJobWorkerOfferWorkerAcceptanceReply';

export interface IWorkerAnonState {
  isBookingOfferSubmitted: boolean;
  isBookingConfirmationSubmitted: boolean;
  isJobInviteSubmitted: boolean;
  isJobInterviewConfirmationSubmitted: boolean;
  isJobOfferAcceptanceSubmitted: boolean;
  isError: boolean;
  errorMessage: string;
  loading: boolean; 
  loadingDialog: boolean;
  bookingOffer: AnonBookingRoleWorkerDto;
  bookingConfirmation: AnonBookingRoleWorkerDto;
  jobInvite: AnonJobWorkerInviteDto;
  interviewConfirmation: AnonJobWorkerInterviewDto;
  offerAcceptance: AnonJobWorkerOfferDto;
}

@Module({ dynamic: true, store, name: 'WorkerAnon', namespaced: true })
class WorkerAnon extends VuexModule implements IWorkerAnonState {
  public isBookingOfferSubmitted = false;
  public isBookingConfirmationSubmitted = false;
  public isJobInviteSubmitted = false;
  public isJobInterviewConfirmationSubmitted = false;
  public isJobOfferAcceptanceSubmitted = false;
  public isError = false; 
  public errorMessage = '';
  public loading = true;
  public loadingDialog = false;
  public bookingOffer: AnonBookingRoleWorkerDto = new AnonBookingRoleWorkerDto();
  public bookingConfirmation: AnonBookingRoleWorkerDto = new AnonBookingRoleWorkerDto();
  public jobInvite: AnonJobWorkerInviteDto = new AnonJobWorkerInviteDto();
  public interviewConfirmation: AnonJobWorkerInterviewDto = new AnonJobWorkerInterviewDto();
  public offerAcceptance: AnonJobWorkerOfferDto = new AnonJobWorkerOfferDto();
    
  @Action
  async getBookingOffer(bookingRoleWorkerId: string) {
    this.setLoading(true);
    await ajax.get(`/api/v1/anon-worker/booking-offers/${bookingRoleWorkerId}`)
    .then(response => {
      this.context.commit("setBookingOffer", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action 
  async updateBookingOffer(request: UpdateAnonWorkerBookingOfferRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/anon-worker/booking-offers/${WorkerAnonModule.bookingOffer.bookingRoleWorkerId}`, request)
    .then(response => {
      this.context.commit("setSubmittedBookingOffer");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getBookingConfirmation(bookingRoleWorkerId: string) {
    this.setLoading(true);
    
    await ajax.get(`/api/v1/anon-worker/booking-confirmations/${bookingRoleWorkerId}`)
    .then(response => {
      this.context.commit("setBookingConfirmation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action 
  async updateBookingConfirmation(request: UpdateAnonWorkerBookingConfirmationRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/anon-worker/booking-confirmations/${WorkerAnonModule.bookingConfirmation.bookingRoleWorkerId}`, request)
    .then(response => {
      this.context.commit("setSubmittedBookingConfirmation");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }


  @Action 
  async optOutOfMarketing(workerId: string) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/anon-worker/marketing-opt-out/${workerId}`)
    .then(response => {
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getJobInvite(jobWorkerId: string) {
    this.setLoading(true);
    
    await ajax.get(`/api/v1/anon-worker/job-invites/${jobWorkerId}`)
    .then(response => {
      this.context.commit("setJobInvite", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoading(false);
    });
  }


  @Action 
  async updateJobInvite(request: UpdateAnonWorkerJobInviteRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/anon-worker/job-invites/${WorkerAnonModule.jobInvite.jobWorkerId}`, request)
    .then(response => {
      this.context.commit("setSubmittedJobInvite");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getInterviewConfirmation(jobWorkerInterviewId: string) {
    this.setLoading(true);
    await ajax.get(`/api/v1/anon-worker/job-interviews/${jobWorkerInterviewId}`)
    .then(response => {
      this.context.commit("setInterviewConfirmation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action 
  async interviewWorkerConfirmation(request: AnonJobWorkerInterviewWorkerConfirmationReply) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/anon-worker/job-interviews/${WorkerAnonModule.interviewConfirmation.jobWorkerInterviewId}`, request)
    .then(response => {
      this.context.commit("setSubmittedInterviewConfirmation");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getOfferAcceptance(jobWorkerOfferId: string) {
    this.setLoading(true);
    await ajax.get(`/api/v1/anon-worker/job-offers/${jobWorkerOfferId}`)
    .then(response => {
      this.context.commit("setOfferAcceptance", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoading(false);
    });
  }


  @Action 
  async offerWorkerAcceptance(request: AnonJobWorkerOfferWorkerAcceptanceReply) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/anon-worker/job-offers/${WorkerAnonModule.offerAcceptance.jobWorkerOfferId}`, request)
    .then(response => {
      this.context.commit("setSubmittedOfferAcceptance");
      this.setLoadingDialog(false);
    })
    .catch(error => {
      this.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async handleApiError(error: any) {

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx. response.Data, response.status and response.headers available
      //For anonymous users we keep it really simple...
      if (error.response.data.code == Errors.Forbidden) {
        this.context.commit("setErrorMessage", new ErrorMessage("Sorry, this link has expired.", error.response.data.code, true));
      } else {
        this.context.commit("setErrorMessage", new ErrorMessage("Sorry, an unexpected error occured.", error.response.data.code, false));
      }
    }    
    this.context.commit("setErrorMessage", new ErrorMessage("Sorry, an unexpected error occured.", "", false));
  }

  @Action
  cleanState() {
      this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = true;
    this.loadingDialog = false;
    this.bookingOffer = new AnonBookingRoleWorkerDto();
    this.bookingConfirmation = new AnonBookingRoleWorkerDto();
    this.jobInvite = new AnonJobWorkerInviteDto();
    this.interviewConfirmation = new AnonJobWorkerInterviewDto();
    this.offerAcceptance = new AnonJobWorkerOfferDto();
    this.isError = false;
    this.errorMessage = '';
    this.isBookingOfferSubmitted = false;
    this.isBookingConfirmationSubmitted = false;
    this.isJobInviteSubmitted = false;
    this.isJobInterviewConfirmationSubmitted = false;
  }

  @Mutation
  private async setErrorMessage(error: ErrorMessage) {
      this.isError = true;
      this.errorMessage = error.message;
  }

  @Mutation
  private async setSubmittedBookingOffer() {
      this.isError = false;
      this.errorMessage = '';
      this.isBookingOfferSubmitted = true;
  }

  @Mutation
  private async setSubmittedBookingConfirmation() {
      this.isError = false;
      this.errorMessage = '';
      this.isBookingConfirmationSubmitted = true;
  }

  @Mutation
  private async setSubmittedJobInvite() {
      this.isError = false;
      this.errorMessage = '';
      this.isJobInviteSubmitted = true;
  }

  @Mutation
  private async setSubmittedInterviewConfirmation() {
      this.isError = false;
      this.errorMessage = '';
      this.isJobInterviewConfirmationSubmitted = true;
  }

  @Mutation
  private async setSubmittedOfferAcceptance() {
      this.isError = false;
      this.errorMessage = '';
      this.isJobOfferAcceptanceSubmitted = true;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoading: boolean) {
      this.loadingDialog = isLoading;
  }
  
  @Mutation
  private async setBookingOffer(bookingOffer: AnonBookingRoleWorkerDto) {
      this.bookingOffer = bookingOffer;
      this.isError = false;
      this.errorMessage = '';
  }

  @Mutation
  private async setBookingConfirmation(bookingConfirmation: AnonBookingRoleWorkerDto) {
    this.bookingConfirmation = bookingConfirmation;
    this.isError = false;
    this.errorMessage = '';
  }

  @Mutation
  private async setJobInvite(jobInvite: AnonJobWorkerInviteDto) {
      this.jobInvite = jobInvite;
      this.isError = false;
      this.errorMessage = '';
  }

  @Mutation
  private async setInterviewConfirmation(interviewConfirmation: AnonJobWorkerInterviewDto) {
      this.interviewConfirmation = interviewConfirmation;
      this.isError = false;
      this.errorMessage = '';
  }

  @Mutation
  private async setOfferAcceptance(offerAcceptance: AnonJobWorkerOfferDto) {
      this.offerAcceptance = offerAcceptance;
      this.isError = false;
      this.errorMessage = '';
  }
}

export const WorkerAnonModule = getModule(WorkerAnon)

